// Aviso, tela de espera e alerta de envio interrompido da pausa planejada. DOM puro, sem React
// nem `toastr`: entra em todos os layouts, inclusive os legados, e precisa funcionar com o banco
// fora do ar. Os estilos ficam em entrypoints/maintenance.css, com as cores do design system.
//
// A tela de espera não substitui a página: cobre e torna `inert` o resto do body, então campos
// preenchidos, arquivos escolhidos e o foco continuam onde estavam.
//
// O aviso não cobre nada: enquanto ele aparece, o <html> recebe BANNER_ATTRIBUTE e a altura medida
// do aviso em BANNER_OFFSET_PROPERTY (muda com a quebra de linha no celular), e o maintenance.css
// desce a página e os cabeçalhos fixos por essa altura.

export type MaintenanceView = {
  showBanner: (startsAt: Date, durationSeconds: number) => void;
  hideBanner: () => void;
  showOverlay: (durationSeconds: number) => void;
  hideOverlay: () => void;
  showUncertain: () => void;
  contains: (target: EventTarget | null) => boolean;
  focusOverlay: () => void;
  destroy: () => void;
};

export const UNCERTAIN_MESSAGE =
  "Uma operação foi interrompida pela manutenção. Confira se ela foi concluída antes de repetir.";
const WAITING_MESSAGE = "Esta tela volta sozinha quando o sistema estiver disponível.";
const DELAYED_MESSAGE = "Está levando mais que o previsto. Continue nesta tela, ela volta sozinha.";
export const BANNER_ATTRIBUTE = "data-planned-maintenance-banner";
export const BANNER_OFFSET_PROPERTY = "--planned-maintenance-offset";
// Folga antes de admitir o atraso: a estimativa é do operador, não um cronômetro.
const DELAY_GRACE_MS = 20_000;

export function formatDuration(seconds: number): string {
  if (seconds === 1) return "1 segundo";
  if (seconds < 90) return `${seconds} segundos`;
  return `${Math.round(seconds / 60)} minutos`;
}

export function formatTime(date: Date): string {
  return date.toLocaleTimeString("pt-BR", { hour: "2-digit", minute: "2-digit" });
}

export function createMaintenanceView({
  doc = document,
  logoUrl = null,
}: {
  doc?: Document;
  logoUrl?: string | null;
} = {}): MaintenanceView {
  let root: HTMLElement | undefined;
  let banner: HTMLElement;
  let overlay: HTMLElement;
  let overlayDuration: HTMLElement;
  let overlayStatus: HTMLElement;
  let uncertain: HTMLElement;
  let delayTimer: ReturnType<typeof setTimeout> | undefined;
  let focusBeforeOverlay: Element | null = null;
  const inertBefore = new Map<HTMLElement, boolean>();
  const observer = new MutationObserver(() => blockPage());
  const bannerResize = typeof ResizeObserver === "function" ? new ResizeObserver(() => syncBannerOffset()) : undefined;

  function element<K extends keyof HTMLElementTagNameMap>(
    tag: K,
    attributes: Record<string, string> = {},
    text = "",
  ): HTMLElementTagNameMap[K] {
    const node = doc.createElement(tag);
    for (const [name, value] of Object.entries(attributes)) node.setAttribute(name, value);
    if (text) node.textContent = text;
    return node;
  }

  function mount(): boolean {
    if (root) return true;
    if (!doc.body) return false;

    root = element("div", { id: "planned-maintenance" });

    banner = element("div", { "data-maintenance-banner": "", role: "status", hidden: "" });

    overlay = element("div", {
      "data-maintenance-overlay": "",
      role: "dialog",
      "aria-modal": "true",
      "aria-labelledby": "planned-maintenance-title",
      tabindex: "-1",
      hidden: "",
    });
    const card = element("div", { class: "maintenance-card" });
    if (logoUrl) card.append(element("img", { class: "maintenance-logo", src: logoUrl, alt: "Hospedin" }));
    overlayDuration = element("p");
    overlayStatus = element("p", { class: "maintenance-status", role: "status", "aria-live": "polite" });
    card.append(
      element("span", { class: "maintenance-spinner", "aria-hidden": "true" }),
      element("h2", { id: "planned-maintenance-title" }, "Manutenção em andamento"),
      overlayDuration,
      overlayStatus,
    );
    overlay.append(card);

    uncertain = element("div", { "data-maintenance-uncertain": "", role: "alert", hidden: "" });
    const dismiss = element("button", { type: "button" }, "Entendi");
    dismiss.addEventListener("click", () => {
      uncertain.hidden = true;
    });
    uncertain.append(element("span", {}, UNCERTAIN_MESSAGE), dismiss);

    root.append(banner, overlay, uncertain);
    doc.body.append(root);
    bannerResize?.observe(banner);
    return true;
  }

  function syncBannerOffset() {
    const html = doc.documentElement;
    if (!root || banner.hidden) {
      html.removeAttribute(BANNER_ATTRIBUTE);
      html.style.removeProperty(BANNER_OFFSET_PROPERTY);
      return;
    }
    html.style.setProperty(BANNER_OFFSET_PROPERTY, `${banner.getBoundingClientRect().height}px`);
    html.setAttribute(BANNER_ATTRIBUTE, "");
  }

  // Modais abertos depois da pausa também entram no inert, pelo observer.
  function blockPage() {
    for (const child of Array.from(doc.body.children)) {
      if (!(child instanceof HTMLElement) || child === root) continue;
      if (!inertBefore.has(child)) inertBefore.set(child, child.hasAttribute("inert"));
      child.setAttribute("inert", "");
    }
  }

  function releasePage() {
    for (const [child, wasInert] of inertBefore) if (!wasInert) child.removeAttribute("inert");
    inertBefore.clear();
  }

  function hideOverlay() {
    if (!root || overlay.hidden) return;
    overlay.hidden = true;
    clearTimeout(delayTimer);
    observer.disconnect();
    releasePage();
    if (focusBeforeOverlay instanceof HTMLElement && focusBeforeOverlay.isConnected) focusBeforeOverlay.focus();
    focusBeforeOverlay = null;
  }

  return {
    showBanner(startsAt, durationSeconds) {
      if (!mount()) return;
      banner.textContent = `Manutenção programada para as ${formatTime(startsAt)}, com duração de cerca de ${formatDuration(durationSeconds)}. Salve seu trabalho antes desse horário.`;
      banner.hidden = false;
      syncBannerOffset();
    },
    hideBanner() {
      // Chamado em toda resposta no estado normal: sair cedo evita recálculo de layout por request.
      if (!root || banner.hidden) return;
      banner.hidden = true;
      syncBannerOffset();
    },
    showOverlay(durationSeconds) {
      if (!mount()) return;
      overlayDuration.textContent = `Deve levar cerca de ${formatDuration(durationSeconds)}. Não feche nem recarregue esta tela: o que você preencheu continua aqui.`;
      if (!overlay.hidden) return;
      focusBeforeOverlay = doc.activeElement;
      banner.hidden = true;
      syncBannerOffset();
      overlayStatus.textContent = WAITING_MESSAGE;
      overlay.hidden = false;
      blockPage();
      observer.observe(doc.body, { childList: true });
      overlay.focus();
      delayTimer = setTimeout(
        () => {
          overlayStatus.textContent = DELAYED_MESSAGE;
        },
        durationSeconds * 1000 + DELAY_GRACE_MS,
      );
    },
    hideOverlay,
    showUncertain() {
      if (mount()) uncertain.hidden = false;
    },
    contains: (target) => !!root && target instanceof Node && root.contains(target),
    focusOverlay() {
      if (root && !overlay.hidden) overlay.focus();
    },
    destroy() {
      hideOverlay();
      bannerResize?.disconnect();
      root?.remove();
      root = undefined;
      syncBannerOffset();
    },
  };
}
